import { useEffect, useRef, useCallback } from "react";

/**
 * Manages focus trapping within an overlay container.
 * Uses the Radix FocusScope primitive under the hood when available,
 * but provides a standalone implementation for non-Radix overlays.
 *
 * On activation: captures the currently-focused element as the return target
 * and moves focus into the container.
 * On deactivation: restores focus to the element that was focused before the
 * trap activated, satisfying WCAG 2.1 AA SC 2.4.3.
 *
 * Usage: wrap overlay content in a container with ref={containerRef}.
 */
export function useFocusTrap(isActive: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  // Capture the element that was focused when the trap activated
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");
    return Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(selector),
    );
  }, []);

  useEffect(() => {
    if (!isActive) {
      // Trap is closing — restore focus to the trigger element
      returnFocusRef.current?.focus();
      returnFocusRef.current = null;
      return;
    }

    // Capture current focus so we can restore it when the trap closes
    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        // Shift+Tab: wrap from first to last
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Tab: wrap from last to first
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    // Focus first focusable element on activation
    requestAnimationFrame(() => {
      const focusable = getFocusableElements();
      if (focusable.length > 0) {
        focusable[0].focus();
      }
    });

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isActive, getFocusableElements]);

  return containerRef;
}
